import type { OAuthRequestView } from '@ftthnms/shared';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Bot, ShieldCheck, ShieldX } from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import { AuthLayout } from '../components/AuthLayout';
import { api, ApiError, dateTime } from '../lib/api';
import { logout, ROLE_LABEL, useMe } from '../lib/session';

/**
 * "Izinkan AI agent?" (MCP OAuth). Reached from `/auth/oauth/authorize` right after a Speedmax ID
 * login (the API insists on a fresh one). Approving sends the browser back to the agent with an
 * authorization code; the agent then acts as this user.
 */
export function OAuthConsentPage({ requestId, error }: { requestId?: string; error?: string }) {
  const me = useMe();
  const [switching, setSwitching] = useState(false);
  const req = useQuery({
    queryKey: ['oauth-request', requestId],
    queryFn: () => api<OAuthRequestView>(`/api/v1/oauth/requests/${encodeURIComponent(requestId!)}`),
    enabled: !!requestId && !error,
    retry: false,
  });
  const decide = useMutation({
    mutationFn: (approve: boolean) =>
      api<{ redirect: string }>(`/api/v1/oauth/requests/${encodeURIComponent(requestId!)}/decision`, {
        method: 'POST',
        body: JSON.stringify({ approve }),
      }),
    onSuccess: ({ redirect }) => window.location.assign(redirect),
  });

  const unauthenticated = [me.error, req.error].some((e) => e instanceof ApiError && e.status === 401);
  useEffect(() => {
    if (unauthenticated)
      window.location.assign(
        `/login?returnTo=${encodeURIComponent(window.location.pathname + window.location.search)}`,
      );
  }, [unauthenticated]);

  const failure =
    error ??
    (!requestId ? 'Permintaan tidak lengkap. Ulangi dari aplikasi AI agent.' : null) ??
    (req.error ? req.error.message : null) ??
    (decide.error ? decide.error.message : null);
  const view = req.data;
  const role = me.data?.primaryRole ? ROLE_LABEL[me.data.primaryRole] : null;

  return (
    <AuthLayout>
      <section className="fn-card fn-card--pad fn-auth__card flex flex-col gap-4">
        {failure ? (
          <>
            <Heading icon={<ShieldX className="i i--xl" aria-hidden />} title="Tidak bisa melanjutkan" />
            <div className="fn-alert fn-alert--danger" role="alert">
              <div className="fn-alert__body">{failure}</div>
            </div>
            <a href="/" className="fn-btn fn-btn--secondary fn-btn--block">
              Ke Dashboard
            </a>
          </>
        ) : !view || !me.data ? (
          <p className="fn-hint" style={{ textAlign: 'center' }}>
            Memuat…
          </p>
        ) : (
          <>
            <Heading
              icon={
                view.allowed ? (
                  <Bot className="i i--xl" aria-hidden />
                ) : (
                  <ShieldX className="i i--xl" aria-hidden />
                )
              }
              title={
                view.allowed
                  ? `Izinkan ${view.clientName} mengakses FTTH NMS?`
                  : 'Peran Anda belum diizinkan memakai AI agent'
              }
            />

            <div className="fn-choice" style={{ cursor: 'default' }}>
              <span>
                <span className="fn-choice__title">
                  {view.account.name}
                  {role ? ` · ${role}` : ''}
                </span>
                <span className="fn-choice__desc">
                  {view.account.username}
                  {view.account.email ? ` · ${view.account.email}` : ''}
                  <br />
                  Login Speedmax ID terverifikasi {dateTime.format(new Date(view.loginAt))}
                </span>
              </span>
            </div>
            <button
              type="button"
              className="fn-btn fn-btn--ghost fn-btn--sm"
              style={{ alignSelf: 'flex-start' }}
              disabled={switching || decide.isPending}
              onClick={() => {
                setSwitching(true);
                // Ends the Speedmax ID session too; the agent then has to be connected again.
                void logout(true, false).catch(() => setSwitching(false));
              }}
            >
              Bukan Anda? Keluar dari Speedmax ID
            </button>

            {view.allowed ? (
              <>
                <p className="fn-card__desc">
                  AI agent ini akan bisa <b>membaca dan mengubah data</b> FTTH NMS atas nama akun di atas,
                  sebatas hak akses dan wilayah peran Anda.
                </p>
                <ul className="fn-card__desc" style={{ paddingLeft: 18, listStyle: 'disc' }}>
                  <li>Semua perubahan oleh agent tercatat di Audit Log dengan nama agent.</li>
                  <li>Hapus hanya memindahkan ke Sampah; agent tidak bisa menghapus permanen.</li>
                  <li>Akses bisa dicabut di menu AI Agent, atau dengan logout.</li>
                </ul>
                <p className="fn-hint">
                  Setelah memilih, browser kembali ke <span className="mono">{view.redirectHost}</span>.
                </p>
                <div className="flex gap-2">
                  <button
                    type="button"
                    className="fn-btn fn-btn--secondary"
                    style={{ flex: 1 }}
                    disabled={decide.isPending}
                    onClick={() => decide.mutate(false)}
                  >
                    Tolak
                  </button>
                  <button
                    type="button"
                    className="fn-btn fn-btn--primary"
                    style={{ flex: 1 }}
                    disabled={decide.isPending}
                    onClick={() => decide.mutate(true)}
                  >
                    <ShieldCheck className="i" aria-hidden />
                    Izinkan
                  </button>
                </div>
              </>
            ) : (
              <>
                <p className="fn-card__desc">
                  Admin FTTH NMS mengatur izin "Akses AI Agent (MCP)" per peran di menu Pengguna &amp; Peran.
                  Hubungi Admin bila Anda memerlukannya.
                </p>
                <button
                  type="button"
                  className="fn-btn fn-btn--secondary fn-btn--block"
                  disabled={decide.isPending}
                  onClick={() => decide.mutate(false)}
                >
                  Kembali ke {view.clientName}
                </button>
              </>
            )}
          </>
        )}
      </section>
    </AuthLayout>
  );
}

function Heading({ icon, title }: { icon: ReactNode; title: string }) {
  return (
    <div className="flex flex-col items-center gap-2 text-center">
      <div className="fn-empty__icon">{icon}</div>
      <h1 className="fn-card__title" style={{ fontSize: 18 }}>
        {title}
      </h1>
    </div>
  );
}
